import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useNotifications from '../../Hooks/useNotifications';

function timeAgo(dateString){
  try{
    const delta = Math.floor((Date.now() - new Date(dateString)) / 1000);
    if (delta < 60) return `${delta}s`;
    if (delta < 3600) return `${Math.floor(delta/60)}m`;
    if (delta < 86400) return `${Math.floor(delta/3600)}h`;
    return `${Math.floor(delta/86400)}d`;
  }catch(e){
    return '';
  }
}

export default function NotificationsPanel({ isOpen, onClose }){
  const { unreadCount, notifications, loading, fetchList, markRead } = useNotifications();

  useEffect(() => {
    if (isOpen) fetchList();
  }, [isOpen, fetchList]);

  // If panel is open and unread count changes, refresh the list so new notifications appear live
  useEffect(() => {
    if (isOpen) fetchList();
  }, [isOpen, unreadCount, fetchList]);

  // Poll the list every 10s while panel is open to show new notifications live
  useEffect(() => {
    if (!isOpen) return;
    const intv = setInterval(() => {
      fetchList();
    }, 10000);
    return () => clearInterval(intv);
  }, [isOpen, fetchList]);

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 mt-2 w-[28rem] bg-white border border-gray-100 shadow-2xl rounded-xl overflow-hidden transform transition ease-out duration-150 origin-top-right">
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-white to-gray-50">
        <div className="flex items-center space-x-3">
          <div className={`${isOpen ? 'bg-yellow-400 text-white' : 'bg-indigo-600 text-white'} rounded-full w-9 h-9 flex items-center justify-center shadow`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 00-9.33-4.94" />
            </svg>
          </div>
          <div>
            <div className="text-sm font-semibold">Notifiche</div>
            <div className="text-xs text-gray-500">{unreadCount} non lette</div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button onClick={() => markRead([])} className={`text-sm px-3 py-1 rounded-md focus:outline-none focus:ring-2 ${isOpen ? 'bg-yellow-400 text-white hover:bg-yellow-500 focus:ring-yellow-200' : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-200'}`}>Segna tutte</button>
          <button onClick={onClose} className="text-sm text-gray-600 hover:text-gray-800">Chiudi</button>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto bg-white">
        {loading && <div className="p-6 text-center text-gray-500">Caricamento...</div>}

        {!loading && notifications.length === 0 && (
          <div className="p-6 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 20h.01" />
            </svg>
            <div className="mt-3 text-sm text-gray-500">Nessuna notifica recente</div>
          </div>
        )}

        {!loading && notifications.map(n => (
          <div key={n.id} className={`flex items-start gap-3 px-4 py-3 border-b hover:bg-gray-50 transition-colors ${n.is_read ? '' : 'bg-gradient-to-r from-yellow-50 to-white'}`}>
            <div className="flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-gray-700">
                {n.title ? n.title.charAt(0).toUpperCase() : 'N'}
              </div>
            </div>

            <div className="w-full">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-semibold break-words">{n.title}</div>
                    {n.data && n.data.level && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${n.data.level === 'critical' ? 'bg-red-100 text-red-800' : n.data.level === 'high' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700'}`}>
                        {n.data.level.toUpperCase()}
                      </span>
                    )}
                  </div>

                  <div className="text-sm text-gray-600 mt-1 break-words">{n.message}</div>
                </div>

                <div className="flex-shrink-0 text-right ml-3">
                  <div className="text-xs text-gray-400">{timeAgo(n.created_at)}</div>
                  {!n.is_read && <button onClick={() => markRead([n.id])} className="mt-2 text-xs text-indigo-600 hover:underline">Segna letta</button>}
                </div>
              </div>

              {n.url && (
                <div className="mt-2">
                  <Link to={n.url} onClick={() => { markRead([n.id]); if (typeof onClose === 'function') onClose(); }} className={`text-xs hover:underline ${isOpen ? 'text-yellow-600' : 'text-indigo-600'}`}>Apri dettagli</Link>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
